import DaySwitch from '../../DaySwitch';
import Divider from '../../Divider';
import NumField from '../../NumField';
import Radio from '../../Radio';
import SimpleTable from '../../SimpeTable';
import { IFormState, Day } from '../../../semshared/utils/lister';
import { addDays } from 'date-fns/fp';
import { CapsuleTabContentBody, CapsuleTabContentHeader, ContentRow } from '../CapsuleComponents';
import Dropdown from '../../DropDown';
import AddRemoveButton from '../../AddRemoveButton';
import { AvailabilityType } from '../../../semshared/availability/availability';
import TimeField from '../../TimePicker';
import { LinkButton } from '../../LinkButton';
import Icon from '../../Icon';
import React, { useEffect } from 'react';
import Button from '../../Button';
import { useTranslation } from 'react-i18next';

interface MeetingRoomsSectionProps {
  formState: IFormState;
  requestFormStateUpdateUserInitiated: (newState: IFormState) => void;
  requestFormStateUpdateAutomatic: (newState: IFormState) => void;
  meetingRoomsDay: any;
  setMeetingRoomsDay: any;
  shadowRoot: any;
  availabilityChecker: AvailabilityType | null;
  setDrawerShow: (s: string) => void;
  setPopupShow: (s: string | null) => void;
}

/**
 * Drawer variant of meeting-room editing with detailed per-room controls.
 * @param props - Day context, manual/automatic update channels, and form state.
 * @returns Full meeting-room editor for advanced workflows.
 */
const MeetingRoomsSectionDrawer = ({ formState, requestFormStateUpdateUserInitiated, requestFormStateUpdateAutomatic, meetingRoomsDay, setMeetingRoomsDay, shadowRoot, availabilityChecker, setPopupShow }: MeetingRoomsSectionProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    // Check if any day has been manually edited
    const isAnyDayManuallyEdited = formState.days.some(day => day.seatCountsManuallyEdited === true);

    // Manual edits disable auto redistribution globally to avoid overriding user seat allocations.
    if (isAnyDayManuallyEdited) {
      return;
    }

    // Update seatCount for all days, not just the currently selected day
    const updatedDays = formState.days.map((day) => {
      // If seat counts have been manually edited for this day, skip it
      if (day.seatCountsManuallyEdited === true) {
        return day;
      }

      const totalGuestsForDay = day.totalGuests || 0;
      const numberOfRooms = day.occupancy?.length || 0;

      let newCalculatedSeatCount: number[];

      if (numberOfRooms === 0) {
        newCalculatedSeatCount = [];
      } else {
        newCalculatedSeatCount = Array(numberOfRooms).fill(0);
        if (totalGuestsForDay > 0) {
          // First room acts as canonical bucket to keep total capacity clearly represented.
          newCalculatedSeatCount[0] = totalGuestsForDay; // First room gets all participants

          if (numberOfRooms > 1) {
            // For subsequent rooms, distribute based on totalGuests / numberOfRooms (rounded up)
            const guestsPerRemainingRoom = Math.ceil(totalGuestsForDay / numberOfRooms);
            for (let i = 1; i < numberOfRooms; i++) {
              newCalculatedSeatCount[i] = guestsPerRemainingRoom;
            }
          }
        }
      }

      // Check if an update is actually needed
      const existingSeatCount = day.seatCount || [];
      const needsUpdate =
        existingSeatCount.length !== newCalculatedSeatCount.length ||
        existingSeatCount.some((sc, idx) => sc !== newCalculatedSeatCount[idx]);

      if (needsUpdate) {
        return {
          ...day,
          seatCount: newCalculatedSeatCount,
          seatCountsManuallyEdited: false,
        };
      }

      return day;
    });

    // Check if any day actually needs an update
    const hasUpdates = updatedDays.some((day, index) => {
      const originalDay = formState.days[index];
      const originalSeatCount = originalDay.seatCount || [];
      const newSeatCount = day.seatCount || [];
      return (
        originalSeatCount.length !== newSeatCount.length ||
        originalSeatCount.some((sc, idx) => sc !== newSeatCount[idx])
      );
    });

    if (hasUpdates) {
      requestFormStateUpdateAutomatic({
        ...formState,
        days: updatedDays,
      });
    }
  }, [
    // Watch all days by serializing the relevant properties
    JSON.stringify(formState.days.map(d => ({
      totalGuests: d.totalGuests,
      occupancyLength: d.occupancy?.length,
      seatCount: d.seatCount,
      seatCountsManuallyEdited: d.seatCountsManuallyEdited,
    }))),
    // requestFormStateUpdateAutomatic is assumed to be stable
  ]);

  return (
    <>
      <CapsuleTabContentHeader>
        <ContentRow>
          <DaySwitch
            value={meetingRoomsDay}
            max={formState.days.length - 1}
            onChange={v => setMeetingRoomsDay(v)}
            date={addDays(meetingRoomsDay, formState.start)}
          />
        </ContentRow>
      </CapsuleTabContentHeader>
      {formState.days[meetingRoomsDay].occupancy.map((m, index) => (
        <div key={index} style={{ backgroundColor: 'white', padding: 15, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <strong style={{ display: 'block', whiteSpace: 'nowrap' }}>{t('widget:table.room') + ' ' + (index + 1)}</strong>
            <button style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }} onClick={() => {
              const isAnyDayManuallyEditedGlobal = formState.days.some(d => d.seatCountsManuallyEdited === true && d !== formState.days[meetingRoomsDay]); // Check if ANY OTHER day is manually edited

              requestFormStateUpdateUserInitiated({
                ...formState,
                days: formState.days.map((d, i) => {
                  if (i === meetingRoomsDay) {
                    const currentTotalGuests = d.totalGuests || 0;
                    const updatedOccupancy = d.occupancy.filter((_, roomIndex) => roomIndex !== index);
                    const updatedSeating = d.seating.filter((_, roomIndex) => roomIndex !== index);
                    let recalculatedSeatCount: number[] = [];
                    const newTotalNumRooms = updatedOccupancy.length;

                    // Determine if this day should operate in manual mode for seat counts.
                    // It's manual if global state (another day) is manual, OR if this day itself was already marked manual.
                    const dayShouldBeManuallyHandled = isAnyDayManuallyEditedGlobal || d.seatCountsManuallyEdited;

                    if (dayShouldBeManuallyHandled) {
                      // Manual mode: preserve other counts, remove the specific one
                      recalculatedSeatCount = d.seatCount.filter((_, roomIndex) => roomIndex !== index);
                    } else {
                      // Automatic mode: redistribute
                      if (newTotalNumRooms > 0) {
                        recalculatedSeatCount = Array(newTotalNumRooms).fill(0);
                        if (currentTotalGuests > 0) {
                          recalculatedSeatCount[0] = currentTotalGuests;
                          if (newTotalNumRooms > 1) {
                            const guestsPerRemainingRoom = Math.ceil(currentTotalGuests / newTotalNumRooms);
                            for (let j = 1; j < newTotalNumRooms; j++) {
                              recalculatedSeatCount[j] = guestsPerRemainingRoom;
                            }
                          }
                        }
                      }
                    }

                    return {
                      ...d,
                      occupancy: updatedOccupancy,
                      seating: updatedSeating,
                      seatCount: recalculatedSeatCount,
                      // If dayShouldBeManuallyHandled is true, it remains/becomes true.
                      // If it was false (automatic), it remains false.
                      seatCountsManuallyEdited: dayShouldBeManuallyHandled,
                    };
                  }
                  return d;
                }),
              });
            }}
            ><Icon name="trash" /></button>
          </div>
          {index === 0 && (
            <div style={{ marginBottom: 10 }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px' }}>
                  <Icon name="checkmark" />
                  <span>{t('widget:roomEquipment.beamer')}</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px' }}>
                  <Icon name="checkmark" />
                  <span>{t('widget:roomEquipment.flipchart')}</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px' }}>
                  <Icon name="checkmark" />
                  <span>{t('widget:roomEquipment.pinboard')}</span>
                </li>
              </ul>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#666', fontWeight: 'bold' }}>{t('widget:labels.seating')}</label>
              <Dropdown
                popupPosition="top"
                shadowRoot={shadowRoot}
                value={formState.days[meetingRoomsDay].seating[index]}
                onChange={value =>
                  requestFormStateUpdateUserInitiated({
                    ...formState,
                    days: formState.days.map((d, i) => {
                      if (i === meetingRoomsDay) {
                        return {
                          ...d,
                          seating: d.seating.map((s, j) => {
                            if (j === index) {
                              return value;
                            }
                            return s;
                          }),
                          seatCount: d.seatCount.map((sc, j) => {
                            if (j === index) {
                              return 10;
                            }
                            return sc;
                          }),
                        };
                      }
                      return d;
                    }),
                  })
                }
                options={[
                  {
                    value: 'UFORM',
                    label: t('widget:labels.uform'),
                    icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/U-form.png`} />,
                  },
                  {
                    value: 'THEATER',
                    label: t('widget:seating.theater'),
                    icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/theater.png`} />,
                  },
                  {
                    value: 'PARLAMENT',
                    label: t('widget:seating.parliament'),
                    icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/parlament.png`} />,
                  },
                  {
                    value: 'CIRCLE',
                    label: t('widget:seating.circle'),
                    icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/kreis.png`} />,
                  },
                  {
                    value: 'BANKETT',
                    label: t('widget:seating.banquet'),
                    icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/bankett.png`} />,
                  },
                  {
                    value: 'COCKTAIL',
                    label: t('widget:seating.cocktail'),
                    icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/cocktail.png`} />,
                  },
                  {
                    value: 'BLOCK',
                    label: t('widget:seating.block'),
                    icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/block.png`} />,
                  },
                ]}
              />
            </div>
            <div style={{ width: '50%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#666', fontWeight: 'bold' }}>{t('widget:labels.participants')}</label>
              <NumField
                value={formState.days[meetingRoomsDay].seatCount[index]}
                min={1}
                onChange={value =>
                  requestFormStateUpdateUserInitiated({
                    ...formState,
                    days: formState.days.map((d, i) => {
                      if (i === meetingRoomsDay) {
                        const newSeatCount = [...d.seatCount];
                        newSeatCount[index] = value;
                        return {
                          ...d,
                          seatCount: newSeatCount,
                          seatCountsManuallyEdited: true,
                        };
                      }
                      return d;
                    }),
                  })
                }
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, gap: 10 }}>
            <div style={{ flexGrow: 1 }}>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '12px', color: '#666', fontWeight: 'bold' }}>{t('widget:timeLabels.from')}</label>
              <TimeField value={formState.days[meetingRoomsDay].occupancy[index].from} onChange={value => {
                requestFormStateUpdateUserInitiated({
                  ...formState,
                  days: formState.days.map((d, i) => {
                    if (i === meetingRoomsDay) {
                      return {
                        ...d,
                        occupancy: d.occupancy.map((s, j) => j === index ? { ...s, from: value } : s)
                      };
                    }
                    return d;
                  }),
                });
              }} />
            </div>
            <div style={{ flexGrow: 1 }}>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '12px', color: '#666', fontWeight: 'bold' }}>{t('widget:timeLabels.to')}</label>
              <TimeField value={formState.days[meetingRoomsDay].occupancy[index].to} onChange={value => {
                requestFormStateUpdateUserInitiated({
                  ...formState,
                  days: formState.days.map((d, i) => {
                    if (i === meetingRoomsDay) {
                      return {
                        ...d,
                        occupancy: d.occupancy.map((s, j) => j === index ? { ...s, to: value } : s)
                      };
                    }
                    return d;
                  }),
                });
              }} />
            </div>
          </div>
        </div>
      ))}
      <br />
      <div style={{ padding: 15 }}>
        <LinkButton
          onClick={() => {
            const isAnyDayManuallyEditedGlobal = formState.days.some(d => d.seatCountsManuallyEdited === true);

            requestFormStateUpdateUserInitiated({
              ...formState,
              days: formState.days.map((d, i) => {
                if (i === meetingRoomsDay) {
                  const currentTotalGuests = d.totalGuests || 0;
                  const oldOccupancy = d.occupancy || [];
                  const oldSeatCount = d.seatCount || [];
                  const oldSeating = d.seating || [];

                  const newOccupancy = [...oldOccupancy, { from: '08:00', to: '18:00' }];
                  const newSeating = [...oldSeating, 'UFORM'];
                  const newTotalNumRooms = newOccupancy.length;
                  let updatedSeatCount = Array(newTotalNumRooms).fill(0);
                  let dayBecomesManuallyEdited = d.seatCountsManuallyEdited; // Preserve current state unless changed

                  if (isAnyDayManuallyEditedGlobal || d.seatCountsManuallyEdited) {
                    // Manual mode: preserve existing, add default for new room
                    updatedSeatCount = [...oldSeatCount];
                    const defaultNewRoomSeatCount = newTotalNumRooms > 0 && currentTotalGuests > 0 ? Math.ceil(currentTotalGuests / newTotalNumRooms) : (currentTotalGuests > 0 ? currentTotalGuests : 0);
                    updatedSeatCount.push(defaultNewRoomSeatCount);
                    dayBecomesManuallyEdited = true;
                  } else {
                    // Automatic mode: redistribute
                    if (currentTotalGuests > 0) {
                      updatedSeatCount[0] = currentTotalGuests;
                      if (newTotalNumRooms > 1) {
                        const guestsPerRemainingRoom = Math.ceil(currentTotalGuests / newTotalNumRooms);
                        for (let j = 1; j < newTotalNumRooms; j++) {
                          updatedSeatCount[j] = guestsPerRemainingRoom;
                        }
                      }
                    }
                    dayBecomesManuallyEdited = false; // Explicitly set to false if auto-distributing
                  }

                  return {
                    ...d,
                    occupancy: newOccupancy,
                    seating: newSeating,
                    seatCount: updatedSeatCount,
                    seatCountsManuallyEdited: dayBecomesManuallyEdited,
                  };
                }
                return d;
              }),
            });
          }}
        >
          {t('widget:buttons.addRoom')}
        </LinkButton>
      </div>
    </>
  );
};

export default MeetingRoomsSectionDrawer;
